import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const FPO_BENEFITS = [
  'Manage member farmers and their farms in one place',
  'Aggregate stock across crops, list with full traceability',
  'Discover bulk buyers across states without intermediaries',
  'Track orders end-to-end: accepted → shipped → delivered',
  'In-app chat and counter-offers per order',
];

const BUYER_BENEFITS = [
  'Browse published listings from verified FPOs across India',
  'Filter by crop, state, district, and minimum order quantity',
  'Multi-line cart, transparent pricing, no hidden fees',
  'Live status timeline on every order you place',
  'Direct chat with the FPO on each order',
];

function CheckIcon({ tone }) {
  return (
    <span
      className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
        tone === 'amber' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
      } ring-1 ring-inset ${tone === 'amber' ? 'ring-amber-200' : 'ring-green-200'}`}
    >
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

function Ornament({ tone }) {
  // Small editorial divider — three diamonds + a line, classic-magazine vibe.
  const color = tone === 'amber' ? 'text-amber-700' : 'text-green-700';
  return (
    <div className={`flex items-center justify-start gap-2 ${color}`}>
      <span className="h-px w-8 bg-current/40" />
      <span className="text-[8px]">◆ ◆ ◆</span>
      <span className="h-px w-8 bg-current/40" />
    </div>
  );
}

function RoleCard({ tag, title, blurb, benefits, ctaLabel, tone, delay = 0 }) {
  const isAmber = tone === 'amber';
  // Surface: layered, with a faint gradient and an inset highlight ring so
  // the cards feel "lifted" rather than flat.
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="group relative"
    >
      {/* Soft halo behind the card */}
      <div
        className={`pointer-events-none absolute -inset-1 rounded-[2rem] opacity-60 blur-2xl transition-opacity duration-500 group-hover:opacity-100 ${
          isAmber ? 'bg-amber-200/40' : 'bg-green-200/40'
        }`}
      />

      <article
        className={`relative flex h-full flex-col overflow-hidden rounded-3xl bg-gradient-to-b p-10 ring-1 transition-all duration-500 ${
          isAmber
            ? 'from-white via-amber-50/40 to-white ring-amber-100/80 hover:ring-amber-200'
            : 'from-white via-green-50/40 to-white ring-green-100/80 hover:ring-green-200'
        }`}
        style={{
          boxShadow:
            '0 1px 0 0 rgba(255,255,255,0.6) inset, 0 30px 60px -30px rgba(15, 60, 30, 0.18), 0 12px 32px -16px rgba(15, 60, 30, 0.10)',
        }}
      >
        {/* Decorative top-right corner mark */}
        <div
          className={`pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30 ${
            isAmber ? 'bg-amber-200' : 'bg-green-200'
          } blur-3xl`}
        />

        {/* Brand-pill */}
        <span
          className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white ${
            isAmber ? 'bg-amber-700' : 'bg-green-800'
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
          {tag}
        </span>

        <h3 className="mt-6 font-serif text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-4xl">
          {title}
        </h3>

        <p className="mt-4 font-serif text-base italic leading-relaxed text-gray-600">{blurb}</p>

        <div className="mt-6">
          <Ornament tone={tone} />
        </div>

        <ul className="mt-6 flex flex-col gap-3.5 text-[15px] text-gray-800">
          {benefits.map((b) => (
            <li key={b} className="flex items-start gap-3 leading-relaxed">
              <CheckIcon tone={tone} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-10 flex items-center gap-4">
          <Link
            to="/signup"
            className={`group/cta inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 ${
              isAmber
                ? 'bg-gradient-to-br from-amber-700 to-amber-800 shadow-amber-900/30 hover:from-amber-600 hover:to-amber-700 hover:shadow-amber-900/40'
                : 'bg-gradient-to-br from-green-700 to-green-900 shadow-green-900/30 hover:from-green-600 hover:to-green-800 hover:shadow-green-900/40'
            }`}
          >
            {ctaLabel}
            <svg
              className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <span className="text-xs text-gray-500">
            No credit card. Free during pilot.
          </span>
        </div>
      </article>
    </motion.div>
  );
}

export default function ForRolesSection() {
  return (
    <section id="for-you" className="relative overflow-hidden bg-gradient-to-b from-white via-green-50/30 to-white py-24 md:py-32">
      {/* Faint dot grid behind everything for the premium-print feel */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.25]"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(22, 101, 52, 0.18) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-green-700">For you</p>
          <h2 className="mt-4 font-serif text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            Which side of the market are you on?
          </h2>
          <p className="mt-5 font-serif text-base italic leading-relaxed text-gray-600 sm:text-lg">
            Two products in one platform — built so the FPO and the buyer never need a middleman to translate between them.
          </p>
          <div className="mt-7 flex justify-center">
            <Ornament tone="green" />
          </div>
        </motion.div>

        <div className="mt-16 grid gap-10 md:grid-cols-2 md:gap-8 lg:gap-12">
          <RoleCard
            tag="For FPOs"
            title="Sell direct to bulk buyers"
            blurb="Atharva replaces commission agents with a verified buyer network — and gives you the operational tools to run the FPO."
            benefits={FPO_BENEFITS}
            ctaLabel="Sign up as an FPO"
            tone="green"
            delay={0}
          />
          <RoleCard
            tag="For Buyers"
            title="Source bulk from verified FPOs"
            blurb="Skip the layers between you and the farmer. Aggregated stock, transparent pricing, and a clear trail from order to delivery."
            benefits={BUYER_BENEFITS}
            ctaLabel="Sign up as a buyer"
            tone="amber"
            delay={0.12}
          />
        </div>
      </div>
    </section>
  );
}
